// Single source of truth for date display formatting across the dashboard.
// Mirrors the rationale in format-money.ts: callsites had drifted into
// `.toLocaleDateString()` with no locale arg (= browser default, which
// renders MM/dd/yyyy on en-US machines and dd/MM/yyyy on pt-BR machines),
// producing inconsistent dates depending on who looked at the page.
//
// Locale pinned to pt-BR (dd/MM/yyyy) per product decision: the operator
// audience is Brazil-anchored, US date format reads as ambiguous to them
// (5/3/2026 = May 3 or March 5?), and the dashboard is monolingual today
// so locale-mixing isn't a concern.
//
// Three exported utils mirror the three shapes in use:
//   • formatDate(iso)        → '03/05/2026'           (date only)
//   • formatDateTime(iso)    → '03/05/2026 14:25'     (audit log row)
//   • formatDateRange(a, b)  → '01/04 – 01/05'        (billing period)
//
// Locale-aware Intl.DateTimeFormat is used directly (not toLocaleDateString
// per call) so the formatter object is allocated once per shape; cheaper
// at scale than the per-call alternative.

const DATE = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

// Range shape (no year) — used in billing period KPI sublabel where the
// year is implicit from context. Matches the `Apr 1 – May 1` shape that
// the prior formatPeriodRange (now removed) produced, but in dd/MM.
const RANGE_PART = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
});

export function formatDate(iso: string): string {
  return DATE.format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  // pt-BR Intl emits "03/05/2026, 14:25" by default — the comma is browser-
  // dependent (some Chromium versions skip it). Normalise to a single space
  // so audit-log rows look the same regardless of viewer.
  return DATE_TIME.format(new Date(iso)).replace(',', '');
}

export function formatDateRange(startIso: string, endIso: string): string {
  return `${RANGE_PART.format(new Date(startIso))} – ${RANGE_PART.format(new Date(endIso))}`;
}

// Single-date short form (dd/MM, no year) — used by chart tick labels in
// usage-helpers.ts where the year is implicit from the surrounding range
// label and including it would cramp the x-axis.
export function formatShortDay(iso: string): string {
  return RANGE_PART.format(new Date(iso));
}
